import type { HelmInspectQuery } from '@features/fields/api/useHelmInspect'
import { type UseFormReturn, useWatch } from 'react-hook-form'
import { useLocalization } from 'shared-ui/localization'
import { planZoneColor, useMapContext, useSelectedId } from 'shared-ui/map'

import type { FormValues } from './formtypes'
import { corridorKey, withOverride } from './overrideKeys'
import {
  InspectStates,
  OverrideRow,
  type PillOption,
  PillToggle,
  useViewOnlyFeatures
} from './shared'

export const usePassableCorridorOptions = (): readonly [
  PillOption<boolean>,
  PillOption<boolean>
] => {
  const { t } = useLocalization()
  return [
    {
      value: true,
      label: t(($) => $.planning.passable),
      subtleColor: 'bg-green-50 text-green-600',
      strongColor: 'bg-green-100 text-green-800'
    },
    {
      value: false,
      label: t(($) => $.planning.impassable),
      subtleColor: 'bg-red-50 text-red-500',
      strongColor: 'bg-red-100 text-red-700'
    }
  ]
}

export const Corridors = ({
  form,
  inspectQuery
}: {
  form: UseFormReturn<FormValues>
  inspectQuery: HelmInspectQuery
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()
  const corridors = inspectQuery.data?.corridors ?? []

  const selectedCorridorId = useSelectedId()
  const passableOptions = usePassableCorridorOptions()

  const corridorOverrides = useWatch({ control: form.control, name: 'corridorOverrides' })
  const setPassable = (key: string, passable: boolean | null) =>
    form.setValue(
      'corridorOverrides',
      withOverride(corridorOverrides, key, passable === null ? undefined : { key, passable }),
      { shouldDirty: true }
    )

  useViewOnlyFeatures('corridor', corridors, (_corridor, i) => ({ color: planZoneColor(i) }))

  return (
    <InspectStates
      query={inspectQuery}
      isEmpty={corridors.length === 0}
      emptyText={t(($) => $.planning.noCorridorsDetected)}
    >
      <div className="flex flex-col gap-3 py-4">
        <p className="text-sm text-gray-500">{t(($) => $.planning.corridorsDescription)}</p>
        <div className="pan-title5">{t(($) => $.planning.corridors)}</div>
        <div className="grid grid-cols-2 gap-2">
          {corridors.map((corridor, corridorIndex) => {
            const key = corridorKey(corridor)
            const passable = corridorOverrides.find((o) => o.key === key)?.passable ?? null
            const isSelected = corridor.id === selectedCorridorId
            return (
              <OverrideRow
                key={corridor.id}
                color={planZoneColor(corridorIndex)}
                label={t(($) => $.planning.corridorNumber, { number: corridorIndex + 1 })}
                isSelected={isSelected}
                onSelect={() => map.drawTool.setSelected(isSelected ? null : corridor.id)}
                isOverridden={passable !== null}
                onReset={() => setPassable(key, null)}
                resetLabel={t(($) => $.planning.keepSeparate)}
              >
                <PillToggle
                  value={passable}
                  options={passableOptions}
                  onChange={(v) => setPassable(key, v)}
                />
              </OverrideRow>
            )
          })}
        </div>
      </div>
    </InspectStates>
  )
}
